import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Field, Sel, inputCls } from "../Form";
import { BuyButton } from "../BuyButton";
import { errMsg, money, useApi } from "../../lib/api";
import { useMe } from "../../lib/me";

const Shell = ({ open, onOpenChange, title, children, testId }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="bg-[#141419] border-[#2d2d38] max-h-[90vh] overflow-y-auto" data-testid={testId}>
      <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
      <div className="space-y-4">{children}</div>
    </DialogContent>
  </Dialog>
);
const Submit = ({ onClick, children, testId }) => (
  <button onClick={onClick} data-testid={testId} className="w-full rounded-[3px] bg-yellow-400 py-2.5 font-bold text-black hover:bg-yellow-300 transition-colors">{children}</button>
);

export const EditRoomDialog = ({ room, onClose, onSaved }) => {
  const api = useApi();
  const [f, setF] = useState({ name: room.name, genre: room.genre || "", description: room.description || "" });
  const save = async () => {
    try { await api.patch(`/rooms/${room.id}`, f); toast.success("Room updated"); onSaved(); onClose(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Shell open onOpenChange={onClose} title={`Edit ${room.name}`} testId="edit-room-dialog">
      <Field label="Name"><input className={inputCls} value={f.name} maxLength={60} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="edit-room-name-input" /></Field>
      <Field label="Genre / focus"><input className={inputCls} value={f.genre} maxLength={40} onChange={(e) => setF({ ...f, genre: e.target.value })} data-testid="edit-room-genre-input" /></Field>
      <Field label="Description"><textarea className={inputCls} rows={3} value={f.description} maxLength={400} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="edit-room-description-input" /></Field>
      <Submit onClick={save} testId="edit-room-save-btn">Save room</Submit>
    </Shell>
  );
};

export const ScheduleShowDialog = ({ open, onOpenChange, rooms, onSaved }) => {
  const api = useApi();
  const { isAdmin } = useMe();
  const [f, setF] = useState({ room_id: "room-1", title: "", description: "", price: "0", when: "", included_with_membership: false });
  const save = async () => {
    if (!f.title || !f.when) return toast.error("Add a title and a start time");
    try {
      await api.post("/shows", { ...f, price: parseFloat(f.price) || 0, scheduled_at: new Date(f.when).toISOString() });
      toast.success("Show scheduled");
      onSaved();
      onOpenChange(false);
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Shell open={open} onOpenChange={onOpenChange} title="Schedule a live show" testId="schedule-show-dialog">
      <Field label="Room"><Sel value={f.room_id} onChange={(v) => setF({ ...f, room_id: v })} testId="show-room-select" options={rooms.map((r) => ({ value: r.id, label: r.name }))} /></Field>
      <Field label="Title"><input className={inputCls} value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="show-title-input" /></Field>
      <Field label="Description"><textarea className={inputCls} rows={2} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="show-description-input" /></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Starts"><input type="datetime-local" className={inputCls} value={f.when} onChange={(e) => setF({ ...f, when: e.target.value })} data-testid="show-when-input" /></Field>
        <Field label="Ticket price (USD)" hint="$0 makes it free"><input type="number" min="0" step="0.5" className={inputCls} value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} data-testid="show-price-input" /></Field>
      </div>
      {isAdmin && (
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.included_with_membership} onChange={(e) => setF({ ...f, included_with_membership: e.target.checked })} data-testid="show-included-checkbox" className="accent-yellow-400" /> Included with membership</label>
      )}
      <Submit onClick={save} testId="show-save-btn">Schedule show</Submit>
    </Shell>
  );
};

export const CourseDialog = ({ open, onOpenChange, rooms, onSaved }) => {
  const api = useApi();
  const { isAdmin } = useMe();
  const [f, setF] = useState({ title: "", description: "", lessons_count: "6", room_id: "", price: "0", included_with_membership: false });
  const save = async () => {
    if (!f.title) return toast.error("Give the course a title");
    try {
      await api.post("/courses", { ...f, room_id: f.room_id || null, lessons_count: parseInt(f.lessons_count, 10) || 1, price: parseFloat(f.price) || 0 });
      toast.success("Course created");
      onSaved();
      onOpenChange(false);
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Shell open={open} onOpenChange={onOpenChange} title="New course" testId="course-dialog">
      <Field label="Title"><input className={inputCls} value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="course-title-input" /></Field>
      <Field label="Description"><textarea className={inputCls} rows={3} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="course-description-input" /></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Lessons"><input type="number" min="1" className={inputCls} value={f.lessons_count} onChange={(e) => setF({ ...f, lessons_count: e.target.value })} data-testid="course-lessons-input" /></Field>
        <Field label="Price (USD)" hint="$0 makes it free"><input type="number" min="0" step="1" className={inputCls} value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} data-testid="course-price-input" /></Field>
      </div>
      <Field label="Room"><Sel value={f.room_id} onChange={(v) => setF({ ...f, room_id: v })} testId="course-room-select" placeholder="Lesson Room" options={rooms.map((r) => ({ value: r.id, label: r.name }))} /></Field>
      {isAdmin && (
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.included_with_membership} onChange={(e) => setF({ ...f, included_with_membership: e.target.checked })} data-testid="course-included-checkbox" className="accent-yellow-400" /> Included with membership</label>
      )}
      <Submit onClick={save} testId="course-save-btn">Create course</Submit>
    </Shell>
  );
};

export const CourseDetailDialog = ({ courseId, onClose, onChanged }) => {
  const api = useApi();
  const [c, setC] = useState(null);
  const load = useCallback(() => api.get(`/courses/${courseId}`).then((r) => setC(r.data))
    .catch((e) => toast.error(errMsg(e, "Couldn't open this course"))), [api, courseId]);
  useEffect(() => { load(); }, [load]);
  const enroll = async () => {
    try { await api.post(`/courses/${courseId}/enroll`); toast.success("You're enrolled"); load(); onChanged(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Shell open onOpenChange={onClose} title={c?.title || "Course"} testId="course-detail-dialog">
      {c && (
        <>
          <p className="text-sm text-zinc-300 whitespace-pre-wrap">{c.description || "No description yet."}</p>
          <p className="mono text-xs text-zinc-400">{c.lessons_count} lessons · taught by {c.teacher_name}</p>
          {c.has_access ? (
            <div className="space-y-2" data-testid="course-plans">
              <p className="mono-label text-[#39ff6a]">✓ You have access</p>
              {c.plans.length === 0 && <p className="text-sm text-zinc-500">Lesson plans will appear here.</p>}
              {c.plans.map((p) => <p key={p.id} className="text-sm">• {p.title}</p>)}
            </div>
          ) : c.price > 0 ? (
            <BuyButton kind="course" itemId={c.id} price={c.price} className="w-full" testId="course-buy-btn" />
          ) : (
            <Submit onClick={enroll} testId="course-enroll-btn">Enroll free</Submit>
          )}
          <p className="text-xs text-zinc-500">{c.price > 0 ? `${money(c.price)} one-time` : "Free"}</p>
        </>
      )}
    </Shell>
  );
};
